/**
 * Recursos da equipe e da conta que o ponteiro de uma novidade sabe apontar: Usuários,
 * Departamentos, Contas vinculadas (conta parceira) e Indique e Ganhe. Ver `spotlightTargets.js`.
 *
 * Nenhuma dessas telas tem URL por aba ou por diálogo. Os campos dos formulários de usuário só
 * existem com o diálogo aberto (o ponteiro aceita um alvo dentro do diálogo aberto, mas a pessoa
 * precisa abri-lo), e o mesmo formulário aparece para criar e para editar: cada campo tem uma
 * marca por desenho (variante), e o ponteiro aponta o que estiver na tela.
 *
 * Indique e Ganhe mora fora de `implementation/:clientId` (`flat`): a tela mostra a indicação da
 * conta da sessão, nunca a de um `:clientId` navegado. A tela do programa Arquitetura de Operação
 * não entra: ela só existe para quem está numa turma.
 */

const NOVO_USUARIO = { key: "users.new-user", text: "Clique em Novo Usuário." };
const MENU_DA_LINHA = { key: "users.row-menu", text: "Abra o menu de três pontos de um usuário e escolha Editar." };
const NOVO_DEPARTAMENTO = { key: "departments.new", text: "Clique em Novo Departamento." };
const MENU_DO_DEPARTAMENTO = { key: "departments.card-menu", text: "Abra o menu de três pontos do departamento e escolha Editar." };

const USUARIOS = "Lista de usuários";
const FORMULARIO = "Formulário de usuário";
const DEPARTAMENTOS = "Departamentos";
const CONTAS = "Contas vinculadas";
const INDIQUE = "Indique e Ganhe";

const campoDoUsuario = (slug, label, hint) => ({
  key: `users.${slug}`,
  navKey: "users",
  group: FORMULARIO,
  label,
  path: "/users",
  requires: "panel",
  minLevel: 50,
  hint: `${hint} Aparece no formulário de novo usuário e no de edição.`,
  steps: [NOVO_USUARIO, MENU_DA_LINHA],
});

export const TEAM_TARGETS = [
  // ── Usuários ────────────────────────────────────────────────────────────
  {
    key: "users.new-user",
    navKey: "users",
    group: USUARIOS,
    label: "Botão Novo Usuário",
    path: "/users",
    requires: "page",
    minLevel: 50,
    hint: "Convida uma pessoa por e-mail, com papel, departamentos e canais. Não existe no shell embutido.",
  },
  {
    key: "users.search",
    navKey: "users",
    group: USUARIOS,
    label: "Busca por nome ou e-mail",
    path: "/users",
    requires: "page",
    minLevel: 50,
    hint: "Filtra a lista de usuários enquanto a pessoa digita.",
  },
  {
    key: "users.status-filter",
    navKey: "users",
    group: USUARIOS,
    label: "Filtro de status",
    path: "/users",
    requires: "page",
    minLevel: 50,
    hint: "Mostra só ativos, pendentes (convite não aceito), inativos ou suspensos.",
  },
  {
    key: "users.list",
    navKey: "users",
    group: USUARIOS,
    label: "Tabela de usuários",
    path: "/users",
    requires: "page",
    minLevel: 50,
    hint: "Quem tem acesso à conta, o papel de cada um e quem ainda não aceitou o convite.",
  },
  {
    key: "users.row-menu",
    navKey: "users",
    group: USUARIOS,
    label: "Menu de três pontos da linha",
    path: "/users",
    requires: "data",
    minLevel: 50,
    hint: "Editar, reenviar o convite, desativar, ver o histórico ou excluir (com repasse dos atendimentos).",
  },
  {
    key: "users.import-legal",
    navKey: "users",
    group: USUARIOS,
    label: "Importar do AdvBox ou do Projuris",
    path: "/users",
    requires: "page",
    minLevel: 50,
    hint: "Traz os usuários do sistema jurídico conectado, com um papel para todos. Só existe com AdvBox ou Projuris conectado.",
  },
  campoDoUsuario("role-select", "Papel do usuário", "O papel define o que a pessoa vê no menu: Visualizador, Agente, Supervisor ou Admin."),
  campoDoUsuario("departments-select", "Departamentos do usuário", "Coloca a pessoa nos departamentos certos para entrar na fila de distribuição deles."),
  campoDoUsuario("channels-visibility", "Canais visíveis ao usuário", "Restringe quais números de WhatsApp e outros canais a pessoa enxerga na Inbox; vazio libera todos."),
  campoDoUsuario("advanced-permissions", "Permissões avançadas", "Libera permissões extras além do papel, como ver o Kanban ou apagar contatos."),

  // ── Departamentos ───────────────────────────────────────────────────────
  {
    key: "departments.new",
    navKey: "departments",
    group: DEPARTAMENTOS,
    label: "Botão Novo Departamento",
    path: "/departments",
    requires: "page",
    minLevel: 50,
    hint: "Cria um departamento com o modo de distribuição de atendimentos e o limite por atendente.",
  },
  {
    key: "departments.card",
    navKey: "departments",
    group: DEPARTAMENTOS,
    label: "Cartão do departamento",
    path: "/departments",
    requires: "data",
    minLevel: 50,
    hint: "Mostra membros, fila e modo de distribuição de cada departamento. Só existe com ao menos um departamento.",
  },
  {
    key: "departments.card-menu",
    navKey: "departments",
    group: DEPARTAMENTOS,
    label: "Menu de três pontos do departamento",
    path: "/departments",
    requires: "data",
    minLevel: 50,
    hint: "Editar, adicionar membro com nível de experiência ou desativar o departamento.",
  },
  {
    key: "departments.members-list",
    navKey: "departments",
    group: DEPARTAMENTOS,
    label: "Botão Ver membros",
    path: "/departments",
    requires: "data",
    minLevel: 50,
    hint: "Expande a lista de membros, com o nível de cada um e o botão de remover. Só em departamento que tem membros.",
  },
  {
    key: "departments.distribution-mode",
    navKey: "departments",
    group: DEPARTAMENTOS,
    label: "Modo de distribuição",
    path: "/departments",
    requires: "panel",
    minLevel: 50,
    hint: "Manual, Round Robin ou Round Robin ponderado: como os atendimentos novos chegam aos atendentes.",
    steps: [NOVO_DEPARTAMENTO],
  },
  {
    key: "departments.max-slots",
    navKey: "departments",
    group: DEPARTAMENTOS,
    label: "Máximo de tickets por agente",
    path: "/departments",
    requires: "panel",
    minLevel: 50,
    hint: "Quantos atendimentos abertos cada atendente pode ter ao mesmo tempo antes de a distribuição pular para o próximo.",
    steps: [NOVO_DEPARTAMENTO],
  },
  {
    key: "departments.distribute-offline",
    navKey: "departments",
    group: DEPARTAMENTOS,
    label: "Distribuir para agentes offline",
    path: "/departments",
    requires: "panel",
    minLevel: 50,
    hint: "Distribui atendimentos mesmo para quem não está online. Só aparece com um modo de distribuição automático.",
    steps: [NOVO_DEPARTAMENTO, { key: "departments.distribution-mode", text: "Escolha Round Robin em Modo de Distribuição." }],
  },
  {
    key: "departments.inactivity-policy",
    navKey: "departments",
    group: DEPARTAMENTOS,
    label: "Regras de inatividade do departamento",
    path: "/departments",
    requires: "panel",
    minLevel: 50,
    hint: "O que fazer com atendimentos parados: contato sem resposta do agente, contato que parou de responder e tickets de bot ou fila parados. Só na edição.",
    steps: [MENU_DO_DEPARTAMENTO],
  },

  // ── Contas vinculadas (conta parceira) ──────────────────────────────────
  {
    key: "child_accounts.list",
    navKey: "child_accounts",
    group: CONTAS,
    label: "Tabela de contas vinculadas",
    path: "/contas",
    requires: "page",
    minLevel: 50,
    hint: "As contas filhas da conta parceira, com contrato, status, plano e integrações.",
  },
  {
    key: "child_accounts.search",
    navKey: "child_accounts",
    group: CONTAS,
    label: "Busca por nome da conta",
    path: "/contas",
    requires: "page",
    minLevel: 50,
    hint: "Filtra as contas vinculadas pelo nome.",
  },
  {
    key: "child_accounts.load-more",
    navKey: "child_accounts",
    group: CONTAS,
    label: "Botão Carregar mais",
    path: "/contas",
    requires: "data",
    minLevel: 50,
    hint: "A tabela traz 25 contas por vez; o botão traz o resto. Só com mais de 25 contas.",
  },

  // ── Indique e Ganhe ─────────────────────────────────────────────────────
  {
    key: "referrals.link",
    navKey: "referrals",
    group: INDIQUE,
    label: "Link de indicação e botão Copiar",
    path: "/indicacoes",
    requires: "data",
    minLevel: 30,
    hint: "O link da conta para compartilhar. Só aparece depois de aceitar o regulamento, em conta elegível.",
  },
  {
    key: "referrals.accept-rules",
    navKey: "referrals",
    group: INDIQUE,
    label: "Botão Liberar meu link",
    path: "/indicacoes",
    requires: "data",
    minLevel: 30,
    hint: "Aceita o regulamento para liberar o link. Só em conta elegível que ainda não aceitou.",
  },
  {
    key: "referrals.rules",
    navKey: "referrals",
    group: INDIQUE,
    label: "Botão Regulamento",
    path: "/indicacoes",
    requires: "data",
    minLevel: 30,
    hint: "Abre o regulamento vigente do programa. Só existe com um regulamento publicado.",
  },
  {
    key: "referrals.how-it-works",
    navKey: "referrals",
    group: INDIQUE,
    label: "Como funciona",
    path: "/indicacoes",
    requires: "page",
    minLevel: 30,
    hint: "Os três passos do programa: compartilhar, o indicado assinar e o crédito entrar na fatura.",
  },
  {
    key: "referrals.reward-tiers",
    navKey: "referrals",
    group: INDIQUE,
    label: "Faixas de recompensa",
    path: "/indicacoes",
    requires: "page",
    minLevel: 30,
    hint: "Quanto mais indicações no mês, maior a faixa (as faixas não se somam); mostra a faixa atual da conta.",
  },
  {
    key: "referrals.monthly-progress",
    navKey: "referrals",
    group: INDIQUE,
    label: "Seu mês (previsão e progresso)",
    path: "/indicacoes",
    requires: "data",
    minLevel: 30,
    hint: "A previsão de crédito do mês e a barra até a próxima faixa. Só em conta elegível.",
  },
  {
    key: "referrals.month-selector",
    navKey: "referrals",
    group: INDIQUE,
    label: "Seletor de mês das indicações",
    path: "/indicacoes",
    requires: "data",
    minLevel: 30,
    hint: "Troca entre o mês atual e os meses fechados e lista as indicações de cada um. Só em conta elegível.",
  },
];
